import { useMemo } from "react";
import { v4 as uuidv4 } from "uuid";

import Image from "components/Image";
import PersonalProfilePhoto from "components/PersonalProfilePhoto";
import TruncateText from "components/TruncateText";

import { capitalizeFirstLetter } from "helpers/capitalizeFirstLetter";
import cn from "lib/cn";

import { TTabeField, TTableRecord } from "../../types";
import { EAccount } from "types/index";

export interface FieldTextProps {
  field?: TTabeField;
  record?: TTableRecord;
}

const FieldText = ({ field, record }: FieldTextProps) => {
  const value = useMemo(() => {
    let val = field?.key ? record?.[field.key] : "";

    if (!field?.key || field.staticValue) {
      val = field?.staticValue || "";
    }

    if (field?.capitalizeText && val) {
      val = capitalizeFirstLetter(val);
    }

    return val;
  }, [record, field]);

  const isBlacklisted = useMemo(() => {
    if (!field?.isPersonalPicture || !field?.blacklistLabelKey) return false;
    const blacklistedField: string = field?.blacklistLabelKey;
    const keys = blacklistedField.split(".");

    let tmpData: any = undefined;
    keys.forEach((key) => {
      const dataToCheck = tmpData !== undefined ? tmpData : record;

      if (dataToCheck?.[key] !== undefined) {
        tmpData = dataToCheck[key];
      }
    });

    if (typeof tmpData === "boolean") {
      return tmpData;
    }

    return false;
  }, [field, record]);

  const accountType = useMemo(() => {
    return record?.[field?.accountTypeKey] || field?.accountType;
  }, [record, field]);

  return (
    <div
      className={cn([
        "text-neutral-gray-900 flex gap-3 items-center",
        field?.className,
      ])}
    >
      {field?.withImage && (
        <div className="h-10 w-8 flex justify-center items-center rounded-full mr-2">
          {field?.isPersonalPicture ? (
            <PersonalProfilePhoto
              alt="profileUrl"
              height={35}
              width={35}
              imageUrl={record?.[field?.imageKey]}
              className="text-base"
              badgeClassName="right-[7px] top-[15px] w-[15px] h-[15px]"
              label={record?.[field?.imageLabelKey]}
              badgeIconSize={{
                height: accountType === EAccount.STAFF ? 13 : 8,
                width: accountType === EAccount.STAFF ? 13 : 8,
              }}
              isBlacklisted={isBlacklisted}
              type={accountType}
            />
          ) : (
            <Image
              alt="profileUrl"
              height={35}
              width={35}
              imageUrl={record?.[field?.imageKey]}
              className="rounded-full bg-gradient-to-t from-gradient-500 to-gradient-200/50 shrink-0	text-white"
              label={record?.[field?.imageLabelKey]}
            />
          )}
        </div>
      )}

      <TruncateText
        id={uuidv4()}
        className="flex"
        text={!value ? "-" : value}
        tooltipWidth={value?.length > 70 ? "18.75rem" : "auto"}
      />
    </div>
  );
};

export default FieldText;
